import { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/Placementdb.css';
import '../styles/FilterStyles.css';
import '../styles/Advanced.css';
import ProfileAvatarpp from '../components/common/Avatar/ProfileAvatarpp';

// Components
import ColumnSelectionModal from '../components/Staffrecord/ColumnSelectionModal';
import DataTable from '../components/JobDashboard/DataTable';
import Pagination from '../components/JobDashboard/Pagination';
import LoadingErrorState from '../components/Staffrecord/LoadingErrorState';
import { useToast } from '../components/Admissionform/ToastManager';
import UserProfileDropdown from '../components/common/Avatar/UserProfileDropdown';


// New Componentsc
import SearchBar from '../components/JobDashboard/SearchBar';
import FilterSummary from '../components/JobDashboard/FilterSummary';
import FiltersPanel from '../components/JobDashboard/FiltersPanel';
import RecordCountCard from '../components/Placementdb/RecordCountCard';

// Hooks and Utils
import { useFilterPersistence } from '../utils/useFilterPersistence';
import { useStudentData, useColumnManagement } from '../utils/jobhooks';
import { PERMANENT_COLUMNS, ALL_COLUMNS } from '../utils/jobconstant';

const JobDashboard = () => {
    const { showSuccess, showError } = useToast();

    // Updated initial filter structure to match controller
    const initialFilters = {
        selectedDepartments: [],
        selectedSkills: [],
        selectedJobLocations: [],
        selectedExperiences: [],
        selectedStatus: ['Open', 'Freeze'],
        selectedCreatedBy: [],
        selectedAllowedBranches: [],
        // ⬅️ UPDATED: Set default dates (last 1 month)
        fromDate: (() => {
            const date = new Date();
            date.setMonth(date.getMonth() - 2);
            return date.toISOString().split('T')[0];
        })(),
        toDate: new Date().toISOString().split('T')[0],
        searchField: 'jobReqId',
        searchValue: '',
        sortBy: 'createdDate',
        sortOrder: 'DESC',
        currentPage: 1,
        recordsPerPage: 10
    };

    // Use the persistence hook with page-specific key
    const { filters, updateFilters, resetFilters, isReady } = useFilterPersistence(initialFilters, 'jobDashboardFilters');

    // Pending filters for "Apply" functionality
    const [pendingDepartments, setPendingDepartments] = useState([]);
    const [pendingSkills, setPendingSkills] = useState([]);
    const [pendingJobLocations, setPendingJobLocations] = useState([]);
    const [pendingExperiences, setPendingExperiences] = useState([]);
    const [pendingStatus, setPendingStatus] = useState(['Open', 'Freeze']);
    const [pendingCreatedBy, setPendingCreatedBy] = useState([]);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);

    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [showMyRequirements, setShowMyRequirements] = useState(false);
    const [customData, setCustomData] = useState([]);
    // Data states for dropdown options
    const [departments, setDepartments] = useState([]);
    const [skills, setSkills] = useState([]);
    const [jobLocations, setJobLocations] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [createdByOptions, setCreatedByOptions] = useState([]);
    const [statusOptions] = useState([
        { value: '', label: 'All Status' },
        { value: 'Open', label: 'Open' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'Closed', label: 'Closed' },
        { value: 'Freeze', label: 'Freeze' }
    ]);
    // After existing pending states, add:
    const [pendingAllowedBranches, setPendingAllowedBranches] = useState([]);
    const [pendingFromDate, setPendingFromDate] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 2);
        return date.toISOString().split('T')[0];
    });
    const [pendingToDate, setPendingToDate] = useState(() => {
        return new Date().toISOString().split('T')[0];
    });

    // Add data state for branches
    const [allowedBranchesOptions, setAllowedBranchesOptions] = useState([]);

    const lastFetchParamsRef = useRef('');
    const alltechDataRef = useRef([]);

    // Initialize pending filters when persisted filters are loaded
    useEffect(() => {
        if (isReady) {
            console.log('Initializing pending filters with:', filters);
            setPendingDepartments(filters.selectedDepartments || []);
            setPendingSkills(filters.selectedSkills || []);
            setPendingJobLocations(filters.selectedJobLocations || []);
            setPendingExperiences(filters.selectedExperiences || []);
            setPendingStatus(filters.selectedStatus || ['Open', 'Freeze']);
            setPendingCreatedBy(filters.selectedCreatedBy || []);
            setPendingAllowedBranches(filters.selectedAllowedBranches || []);  // ⬅️ NEW
            setPendingFromDate(filters.fromDate || (() => {
                const date = new Date();
                date.setMonth(date.getMonth() - 2);
                return date.toISOString().split('T')[0];
            })());

            setPendingToDate(filters.toDate || new Date().toISOString().split('T')[0]);
        }
    }, [isReady]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(filters.searchValue || '');
        }, 1000);
        return () => clearTimeout(timer);
    }, [filters.searchValue]);

    // Custom hooks
    const {
        data,
        loading,
        error,
        pagination,
        fetchStudentRecords,
        changePage,
        changeItemsPerPage
    } = useStudentData();

    const {
        selectedColumns,
        tempSelectedColumns,
        selectAll,
        isModalOpen,
        toggleSelectAll,
        toggleColumnSelection,
        confirmColumnSelection,
        cancelColumnSelection,
        openColumnModal,
        closeColumnModal
    } = useColumnManagement(PERMANENT_COLUMNS, ALL_COLUMNS);

    const searchableColumns = ALL_COLUMNS.filter(column =>
        selectedColumns.includes(column.key)
    );


    // Update skills when department changes (Integrated logic here)
    useEffect(() => {
        const alltechData = alltechDataRef.current;

        if (pendingDepartments.length > 0 && pendingDepartments[0]) {
            const department = pendingDepartments[0];

            if (alltechData.length === 0) return;

            const deptSkills = alltechData
                .filter(item => item.Department === department && item.skillKnown)
                .map(item => item.skillKnown)
                .filter(Boolean);

            const uniqueSkills = [...new Set(deptSkills)];
            setSkills(uniqueSkills);

            // Clear selected skills if they don't exist in new skills list
            setPendingSkills(prev => {
                if (prev.length > 0 && !uniqueSkills.some(skill => prev.includes(skill))) {
                    return [];
                }
                return prev;
            });
        } else {
            // Clear skills when no department is selected
            setSkills([]);
            setPendingSkills([]);
        }
    }, [pendingDepartments]); // Only pendingDepartments as dependency

    // Single useEffect for fetching initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const token = localStorage.getItem('token');

                // Fetch alltech data only once
                const alltechResponse = await fetch(`${process.env.REACT_APP_API_URL}/alltech`, {
                    method: 'GET',
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                        'Content-Type': 'application/json'
                    }
                });

                if (alltechResponse.ok) {
                    const result = await alltechResponse.json();
                    if (result.success && result.data && Array.isArray(result.data)) {
                        // Store the data in ref for later use
                        alltechDataRef.current = result.data;

                        // Extract unique departments
                        const uniqueDepartments = [...new Set(result.data.map(item => item.Department))].filter(Boolean);
                        setDepartments(uniqueDepartments);

                        // REMOVED: updateSkillsForDepartment call
                        // The useEffect above will handle skills based on pendingDepartments
                    }
                }

                // Fetch created by users
                const usersResponse = await fetch(`${process.env.REACT_APP_API_URL}/placement-officer`, {
                    method: 'GET',
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                        'Content-Type': 'application/json'
                    }
                });

                if (usersResponse.ok) {
                    const usersData = await usersResponse.json();
                    if (Array.isArray(usersData)) {
                        const userNames = usersData.map(user => user).filter(Boolean);
                        setCreatedByOptions(userNames);
                    }
                }

            } catch (error) {
                console.error('Error fetching initial data:', error);
                showError('Error loading filter options.');
            }
        };

        fetchInitialData();
    }, [showError]);


    const handleRequirementsToggle = async () => {

        if (showMyRequirements) {

            setShowMyRequirements(false);
            setCustomData([]);

            return;
        }

        try {

            const token =
                localStorage.getItem("token");

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/my-open-requirements`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const result =
                await response.json();

            if (result.success) {

                setCustomData(result.data);
                setShowMyRequirements(true);
            }

        } catch (error) {

            console.error(error);
        }
    };  
    // Fetch static data
    useEffect(() => {
        // Location data
        const locationData = [
            'Bangalore', 'Chennai', 'Hosur', 'Trichy', 'Madurai', 'Hyderabad', 'Pune', 'Mumbai', 'Gurgaon',
            'Noida', 'Kolkata', 'Ahmedabad', 'Trivandrum', 'Coimbatore',
            'Mohali', 'Indore', 'Jaipur', 'Nagpur',
        ];
        setJobLocations(locationData);

        // Experience data
        const experienceData = [
            'Fresher', '<1year', '1-2years',
            '2-3years', '>3years'
        ];
        setExperiences(experienceData);
    }, []);
    // Add this after the existing useEffect for static data (locations and experiences)
    useEffect(() => {
        // Branch data
        const branchData = [
            'Velachery', 'Vadapalani', 'Tambaram', 'Poonamallee', 'Ambattur', 'Guduvanchery', 'Cross Cut',
            'Marathahalli', 'Gandhipuram', 'Malumichampatti', 'Hosur', 'Saravanampatti', 'Thudiyalur', 'Bommasantra',
            'Tiruppur', 'Kanchipuram'
        ];
        setAllowedBranchesOptions(branchData);
    }, []);
    const SEARCHABLE_FIELDS = [
        { key: 'jobReqId', label: 'Job Req ID' },
        { key: 'jobTitle', label: 'Job Title' },
        { key: 'companyName', label: 'Company Name' },
        { key: 'jobLocation', label: 'Job Location' },
        { key: 'interviewType', label: 'Interview Type' },
    ];
    // Main data fetching useEffect
    useEffect(() => {
        if (!isReady) return;

        const currentParams = JSON.stringify({
            page: filters.currentPage,
            limit: filters.recordsPerPage,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            searchField: filters.searchField,
            searchValue: debouncedSearchValue,
            jobReqStatus: filters.selectedStatus,
            department: filters.selectedDepartments,
            skillSet: filters.selectedSkills,
            experienceRequired: filters.selectedExperiences,
            jobLocation: filters.selectedJobLocations,
            createdBy: filters.selectedCreatedBy,
            allowedBranches: filters.selectedAllowedBranches,  // ⬅️ NEW
            fromDate: filters.fromDate,                         // ⬅️ NEW
            toDate: filters.toDate                              // ⬅️ NEW
        });

        if (currentParams === lastFetchParamsRef.current) {
            console.log('Skipping fetch - params unchanged');
            return;
        }

        let isCancelled = false;

        const fetchData = async () => {
            const searchParams = {
                page: filters.currentPage || 1,
                limit: filters.recordsPerPage || 10,
                sortBy: filters.sortBy || 'createdDate',
                sortOrder: filters.sortOrder || 'DESC'
            };

            if (debouncedSearchValue && debouncedSearchValue.trim()) {
                searchParams.searchField = filters.searchField;
                searchParams.searchValue = debouncedSearchValue.trim();
            }

            const addFilterParam = (paramName, filterArray) => {
                if (filterArray && filterArray.length > 0) {
                    searchParams[paramName] = filterArray.join(',');
                }
            };

            addFilterParam('department', filters.selectedDepartments);
            addFilterParam('skillSet', filters.selectedSkills);
            addFilterParam('experienceRequired', filters.selectedExperiences);
            addFilterParam('jobLocation', filters.selectedJobLocations);
            addFilterParam('createdBy', filters.selectedCreatedBy);
            addFilterParam('allowedBranches', filters.selectedAllowedBranches);  // ⬅️ NEW

            if (filters.selectedStatus && filters.selectedStatus.length > 0) {
                searchParams.jobReqStatus = filters.selectedStatus;
            } else {
                searchParams.jobReqStatus = 'All';
            }

            // ⬅️ NEW: Add date filters
            if (filters.fromDate) {
                searchParams.fromDate = filters.fromDate;
            }
            if (filters.toDate) {
                searchParams.toDate = filters.toDate;
            }

            if (!isCancelled) {
                console.log('Fetching with filters:', searchParams);
                lastFetchParamsRef.current = currentParams;
                await fetchStudentRecords(searchParams);
            }
        };

        fetchData();

        return () => {
            isCancelled = true;
        };
    }, [
        isReady,
        debouncedSearchValue,
        filters.searchField,
        filters.currentPage,
        filters.recordsPerPage,
        filters.sortBy,
        filters.sortOrder,
        filters.selectedDepartments,
        filters.selectedSkills,
        filters.selectedExperiences,
        filters.selectedJobLocations,
        filters.selectedStatus,
        filters.selectedCreatedBy,
        filters.selectedAllowedBranches,  // ⬅️ NEW
        filters.fromDate,                 // ⬅️ NEW
        filters.toDate,                   // ⬅️ NEW
        fetchStudentRecords
    ]);

    // Apply filters
    const handleApplyFilters = async () => {
        setIsApplyingFilters(true);

        try {
            updateFilters({
                selectedDepartments: [...pendingDepartments],
                selectedSkills: [...pendingSkills],
                selectedJobLocations: [...pendingJobLocations],
                selectedExperiences: [...pendingExperiences],
                selectedStatus: pendingStatus,
                selectedCreatedBy: [...pendingCreatedBy],
                selectedAllowedBranches: [...pendingAllowedBranches],
                fromDate: pendingFromDate,
                toDate: pendingToDate,
                currentPage: 1
            });

            showSuccess('Filters applied successfully!');
        } catch (error) {
            console.error('Error applying filters:', error);
            showError('Failed to apply filters. Please try again.');
        } finally {
            setIsApplyingFilters(false);
        }
    };

    // Check for pending changes
    const safeArray = (val) => Array.isArray(val) ? val : [];

    const hasPendingChanges = () => {
        return (
            JSON.stringify((pendingDepartments || []).slice().sort()) !== JSON.stringify((filters.selectedDepartments || []).slice().sort()) ||
            JSON.stringify((pendingSkills || []).slice().sort()) !== JSON.stringify((filters.selectedSkills || []).slice().sort()) ||
            JSON.stringify((pendingJobLocations || []).slice().sort()) !== JSON.stringify((filters.selectedJobLocations || []).slice().sort()) ||
            JSON.stringify((pendingExperiences || []).slice().sort()) !== JSON.stringify((filters.selectedExperiences || []).slice().sort()) ||

            JSON.stringify(safeArray(pendingStatus).slice().sort()) !==
            JSON.stringify(safeArray(filters.selectedStatus).slice().sort()) ||

            JSON.stringify((pendingCreatedBy || []).slice().sort()) !== JSON.stringify((filters.selectedCreatedBy || []).slice().sort()) ||
            JSON.stringify((pendingAllowedBranches || []).slice().sort()) !== JSON.stringify((filters.selectedAllowedBranches || []).slice().sort()) ||

            pendingFromDate !== (filters.fromDate || '') ||
            pendingToDate !== (filters.toDate || '')
        );
    };
    // Clear all filters
    const clearAllFilters = () => {
        const defaultFromDate = (() => {
            const date = new Date();
            date.setMonth(date.getMonth() - 2);
            return date.toISOString().split('T')[0];
        })();
        const defaultToDate = new Date().toISOString().split('T')[0];

        setPendingDepartments([]);
        setPendingSkills([]);
        setPendingJobLocations([]);
        setPendingExperiences([]);
        setPendingStatus(['Open', 'Freeze']);
        setPendingCreatedBy([]);
        setPendingAllowedBranches([]);
        setPendingFromDate(defaultFromDate);  // ⬅️ UPDATED: Set to default
        setPendingToDate(defaultToDate);      // ⬅️ UPDATED: Set to default

        resetFilters();
    };

    // Build current filters utility function
    const buildCurrentFilters = useCallback(() => {
        const currentFilters = {
            page: filters.currentPage || 1,
            limit: filters.recordsPerPage || 10,
            sortBy: filters.sortBy || 'createdDate',
            sortOrder: filters.sortOrder || 'DESC'
        };

        if (filters.searchValue && filters.searchValue.trim()) {
            currentFilters.searchField = filters.searchField;
            currentFilters.searchValue = filters.searchValue.trim();
        }

        if (filters.selectedDepartments?.length > 0) {
            currentFilters.department = filters.selectedDepartments.join(',');
        }
        if (filters.selectedSkills?.length > 0) {
            currentFilters.skillSet = filters.selectedSkills.join(',');
        }
        if (filters.selectedExperiences?.length > 0) {
            currentFilters.experienceRequired = filters.selectedExperiences.join(',');
        }
        if (filters.selectedJobLocations?.length > 0) {
            currentFilters.jobLocation = filters.selectedJobLocations.join(',');
        }
        if (filters.selectedStatus === '' || filters.selectedStatus) {
            currentFilters.jobReqStatus = filters.selectedStatus === '' ? 'All' : filters.selectedStatus;
        }
        if (filters.selectedCreatedBy?.length > 0) {
            currentFilters.createdBy = filters.selectedCreatedBy.join(',');
        }
        // ⬅️ ADD: allowedBranches filter
        if (filters.selectedAllowedBranches?.length > 0) {
            currentFilters.allowedBranches = filters.selectedAllowedBranches.join(',');
        }
        // ⬅️ ADD: Date filters
        if (filters.fromDate) {
            currentFilters.fromDate = filters.fromDate;
        }
        if (filters.toDate) {
            currentFilters.toDate = filters.toDate;
        }

        return currentFilters;
    }, [filters]);

    // Page change handler
    const handlePageChange = async (page) => {
        if (page >= 1 && page <= pagination.totalPages && page !== pagination.currentPage) {
            const currentFilters = buildCurrentFilters();
            await changePage(page, currentFilters);
            updateFilters({ currentPage: page });
        }
    };

    const handleItemsPerPageChange = async (newLimit) => {
        const currentFilters = buildCurrentFilters();
        await changeItemsPerPage(newLimit, currentFilters);
        updateFilters({ recordsPerPage: newLimit, currentPage: 1 });
    };

    const loadingErrorComponent = LoadingErrorState({
        loading,
        error,
        onRetry: () => fetchStudentRecords({
            page: filters.currentPage || 1,
            limit: filters.recordsPerPage || 10
        })
    });

    if (loadingErrorComponent) {
        return loadingErrorComponent;
    }

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <h1 className="dashboard-heading">JOB DASHBOARD</h1>
                <div className="flex items-center gap-4">
                    <UserProfileDropdown />
                    <ProfileAvatarpp />
                </div>
            </div>

            <RecordCountCard
                currentCount={
                    showMyRequirements
                        ? customData.length
                        : (
                            pagination.recordsOnCurrentPage ||
                            data.length
                        )
                }

                totalCount={
                    showMyRequirements
                        ? customData.length
                        : pagination.totalRecords
                }
            />

            <div className='head'>
                <h3>Search Job Requirements</h3>
            </div>

            <FilterSummary
                filters={filters}
                updateFilters={updateFilters}
                setPendingDepartments={setPendingDepartments}
                setPendingSkills={setPendingSkills}
                setPendingJobLocations={setPendingJobLocations}
                setPendingExperiences={setPendingExperiences}
                setPendingStatus={setPendingStatus}
                setPendingCreatedBy={setPendingCreatedBy}
                setPendingAllowedBranches={setPendingAllowedBranches}  // ⬅️ ADD THIS
                setPendingFromDate={setPendingFromDate}                 // ⬅️ ADD THIS
                setPendingToDate={setPendingToDate}
                clearAllFilters={clearAllFilters}
            />

            <FiltersPanel
                // Data for dropdowns
                departments={departments}
                skills={skills}
                jobLocations={jobLocations}
                experiences={experiences}
                statusOptions={statusOptions}
                createdByOptions={createdByOptions}
                allowedBranchesOptions={allowedBranchesOptions}  // ⬅️ NEW

                // Pending states
                pendingDepartments={pendingDepartments}
                pendingSkills={pendingSkills}
                pendingJobLocations={pendingJobLocations}
                pendingExperiences={pendingExperiences}
                pendingStatus={pendingStatus}
                pendingCreatedBy={pendingCreatedBy}
                pendingAllowedBranches={pendingAllowedBranches}  // ⬅️ NEW
                pendingFromDate={pendingFromDate}                 // ⬅️ NEW
                pendingToDate={pendingToDate}                     // ⬅️ NEW
                showMyRequirements={showMyRequirements}
                handleMyOpenRequirements={handleRequirementsToggle}
                setPendingDepartments={setPendingDepartments}
                setPendingSkills={setPendingSkills}
                setPendingJobLocations={setPendingJobLocations}
                setPendingExperiences={setPendingExperiences}
                setPendingStatus={setPendingStatus}
                setPendingCreatedBy={setPendingCreatedBy}
                setPendingAllowedBranches={setPendingAllowedBranches}  // ⬅️ NEW
                setPendingFromDate={setPendingFromDate}                 // ⬅️ NEW
                setPendingToDate={setPendingToDate}                     // ⬅️ NEW

                // Actions
                clearAllFilters={clearAllFilters}
                handleApplyFilters={handleApplyFilters}
                isApplyingFilters={isApplyingFilters}
                hasPendingChanges={hasPendingChanges}
            />

            <SearchBar
                searchField={filters.searchField}
                search={filters.searchValue}
                searchableColumns={SEARCHABLE_FIELDS}
                onFieldChange={(value) => updateFilters({ searchField: value })}
                onSearchChange={(value) => updateFilters({ searchValue: value })}
            />

            <>
                <DataTable
                    currentItems={
                        showMyRequirements
                            ? customData
                            : data
                    } selectedColumns={
                        filters.selectedStatus === 'Closed'
                            ? [...selectedColumns, 'totalSelectionCount'].filter((v, i, a) => a.indexOf(v) === i)
                            : selectedColumns.filter(col => col !== 'totalSelectionCount')
                    }
                    indexOfFirstItem={0}
                />
                {
                    !showMyRequirements && (
                        <Pagination
                            currentPage={pagination.currentPage}
                            totalPages={pagination.totalPages}
                            itemsPerPage={filters.recordsPerPage}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            onOpenColumnModal={openColumnModal}
                            buildFilters={buildCurrentFilters}
                        />
                    )
                }
            </>

            <ColumnSelectionModal
                isOpen={isModalOpen}
                tempSelectedColumns={tempSelectedColumns}
                selectAll={selectAll}
                onToggleSelectAll={toggleSelectAll}
                onToggleColumn={toggleColumnSelection}
                onConfirm={confirmColumnSelection}
                onCancel={cancelColumnSelection}
                onClose={closeColumnModal}
            />
        </div>
    );
};

export default JobDashboard;